import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Button, TextField, Alert, Divider, CircularProgress, Chip, Checkbox, FormControlLabel } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const NAVY = '#1a237e';
const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function SignChangeOrder() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [signedName, setSignedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid signing link'); setLoading(false); return; }
    api.get('/change-orders/sign/' + token)
      .then(res => { setCo(res.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.error || 'Invalid or expired signing link');
        setLoading(false);
      });
  }, [token]);

  const handleSign = async () => {
    if (!signedName || !agreed) return;
    setSubmitting(true);
    try {
      await api.put('/change-orders/sign/' + token, { signed_name: signedName });
      setSigned(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign change order');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <CircularProgress />
    </Box>
  );

  if (error && !co) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ maxWidth: 500, p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: NAVY, fontWeight: 700, mb: 2 }}>Yaplar</Typography>
        <Alert severity="error">{error}</Alert>
      </Card>
    </Box>
  );

  if (signed) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ maxWidth: 500, p: 4, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>Change order signed!</Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          {co.co_number} has been signed successfully. The contract amount will be adjusted once both parties have signed.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ color: '#999' }}>You can close this page.</Typography>
      </Card>
    </Box>
  );

  const isIncrease = ['increase', 'scope_addition', 'price_adjustment'].includes(co.type);
  const typeLabels = {
    increase: 'Price increase', decrease: 'Price decrease',
    scope_addition: 'Scope addition', scope_reduction: 'Scope reduction',
    timeline_extension: 'Timeline extension', price_adjustment: 'Price adjustment',
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa', py: 4 }}>
      <Box sx={{ maxWidth: 600, mx: 'auto', px: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#ff6f00', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 18 }}>C</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Yaplar</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#666' }}>Change order signature request</Typography>
        </Box>

        <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>{co.co_number}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>{co.project_name || co.project_address}</Typography>
            </Box>
            <Chip label={typeLabels[co.type] || co.type}
              sx={{ bgcolor: isIncrease ? '#fff3e0' : '#e8f5e9', color: isIncrease ? '#e65100' : '#2e7d32', fontWeight: 600 }} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{co.title}</Typography>
          {co.description && <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>{co.description}</Typography>}
          <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#999' }}>Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: isIncrease ? '#e65100' : '#2e7d32' }}>
                {isIncrease ? '+' : '-'}{money(co.amount)}
              </Typography>
            </Box>
            {co.days_change !== 0 && co.days_change && (
              <Box>
                <Typography variant="caption" sx={{ color: '#999' }}>Timeline change</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{co.days_change > 0 ? '+' : ''}{co.days_change} days</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={{ color: '#999' }}>Current contract</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{money(co.contract_amount)}</Typography>
            </Box>
          </Box>
          {co.file_url && (
            <Box sx={{ mt: 2 }}>
              <Button size="small" variant="outlined" onClick={() => window.open(co.file_url, '_blank')}>View attached document</Button>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: co.homeowner_signed_at ? '#2e7d32' : '#ccc' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>Owner: {co.homeowner_signed_at ? 'Signed by ' + co.homeowner_signed_name : 'Pending'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: co.contractor_signed_at ? '#2e7d32' : '#ccc' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>Contractor: {co.contractor_signed_at ? 'Signed by ' + co.contractor_signed_name : 'Pending'}</Typography>
            </Box>
          </Box>
        </Card>

        <Card sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DrawIcon sx={{ color: NAVY }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>Sign this change order</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Type your full name" value={signedName} onChange={e => setSignedName(e.target.value)} sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} />}
            label={<Typography variant="body2" sx={{ color: '#555' }}>By typing my name and checking this box, I approve this change order and agree to the modified contract terms.</Typography>}
          />
          <Button fullWidth variant="contained" size="large" disabled={!signedName || !agreed || submitting} onClick={handleSign}
            sx={{ mt: 2, bgcolor: NAVY, '&:hover': { bgcolor: '#0d1453' }, py: 1.5 }}>
            {submitting ? 'Signing...' : 'Sign change order'}
          </Button>
        </Card>
      </Box>
    </Box>
  );
}
